import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';
import type { LngLatBounds, Map as MapboxMap } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import {
  useBrowserStore,
  useEditingIngredientStore,
  useItineraryStore,
  useRouterStore,
  useStatsigEventsStore,
  useTripStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import type {
  ProductSelectionMapData,
  PriceMarker,
  PriceMarkerType,
  LatLongPoint,
} from '@apps/main/new-desktop/modules/newMaps';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';

import type { Marker } from '@libs/maps';
import type { IngredientId } from '@libs/trip-planner-interfaces';

import type {
  HighlightedHotel,
  HotelMapCardData,
  HotelMapCardType,
} from '../../models';

import {
  getHighlightedHotel,
  getHotelMapCardData,
} from './hotelSelectionMapStoreLogic';

const ZOOM_DELAY_MS = 2000;

export const useHotelSelectionMapStore = defineStore(
  'HotelSelectionMapStore',
  () => {
    const recipeStore = useRecipeStore();
    const tripStore = useTripStore();
    const editingIngredientStore = useEditingIngredientStore();
    const statsigEventsStore = useStatsigEventsStore();
    const routerStore = useRouterStore();
    const mapStore = useMapStore();
    const browserStore = useBrowserStore();
    const itineraryStore = useItineraryStore();

    // State
    const highlightedHotel = ref<HighlightedHotel | undefined>(undefined);
    const isFullScreen = ref<boolean>(false);
    const savedBounds = ref<LngLatBounds | undefined>(undefined);
    const mapRef = ref<MapboxMap>();
    const inspectedRef = ref();
    const previouslySeen = ref<Record<string, boolean>>({});

    // Actions
    function resetSavedBound(): void {
      savedBounds.value = undefined;
    }

    function zoomOnMarkerBounds(): void {
      const bounds = new mapboxgl.LngLatBounds();
      setTimeout(() => {
        const markers = mapboxProperties.value?.markers;
        if (!markers) {
          return;
        }
        markers.forEach((marker) => {
          bounds.extend([marker.point.longitude, marker.point.latitude]);
        });
        mapRef.value?.fitBounds(bounds, { padding: 24, duration: 1000 });
      }, ZOOM_DELAY_MS);
    }

    function onToggleFullScreen(value: boolean): void {
      isFullScreen.value = value;
    }

    function showHotelMapCard(
      ingredientId: IngredientId,
      hotelCode: string
    ): void {
      const ingredientIndex = recipeStore.getIngredientIndex(ingredientId);
      highlightedHotel.value = {
        ingredientId: ingredientId,
        ingredientIndex,
        hotelCode,
        showHotelMapCard: true,
        position: undefined,
      };
      statsigEventsStore.logHotelSelectionMapMarkerClicked({
        mapSize: hotelMapCardType.value,
        hotelCode,
      });
    }

    async function hotelCardClicked(): Promise<void> {
      if (!highlightedHotel.value || !hotelMapCardData.value) {
        throw new Error('Not possible!');
      }

      const hotelId = highlightedHotel.value;
      const ingredientIndex = recipeStore.getIngredientIndex(
        hotelId.ingredientId
      );
      await routerStore.navigateToHotelDetailsInHotelSelection(
        { ingredientIndex },
        hotelId.hotelCode
      );
    }

    function hideHotelCard(): void {
      highlightedHotel.value = undefined;
    }

    function hightlightOnMap(data: {
      hotelCode: string | undefined;
      position: LatLongPoint | undefined;
    }): void {
      const ingredientId =
        editingIngredientStore.editingStayIngredient?.ingredientId;

      if (ingredientId) {
        const ingredientIndex = recipeStore.getIngredientIndex(ingredientId);
        highlightedHotel.value = getHighlightedHotel(
          data.hotelCode,
          ingredientId,
          ingredientIndex,
          data.position
        );
      }
    }

    function destroyMap(): void {
      mapStore.updateMapShouldCenterOnMarkers(true);
    }

    function inspect(ingredientId: IngredientId, hotelCode: string): void {
      inspectedRef.value = { ingredientId, hotelCode };
      previouslySeen.value[`${ingredientId}${hotelCode}`] = true;
    }

    function resetInspect(): void {
      inspectedRef.value = undefined;
    }

    function resetMapInstance(): void {
      mapRef.value = undefined;
    }

    // Getters
    const mapboxProperties = computed<ProductSelectionMapData>(() => {
      if (
        !mapStore.markers.length ||
        !editingIngredientStore.editingStayIngredient
      ) {
        return {
          markers: [],
          includedMarkers: [],
          selectedMarkers: [],
          highlightMarker: highlightedHotel.value?.position,
        };
      }

      const hotelIngredient =
        editingIngredientStore.editingStayIngredient as HotelIngredient;
      const heartedHotelCodes = hotelIngredient.heartedHotelCodes;
      const hotelFromItinerary = itineraryStore.getHotelIngredientResponse(
        hotelIngredient.ingredientId
      );

      if (!hotelFromItinerary) {
        return {
          markers: [],
          includedMarkers: [],
          selectedMarkers: [],
          highlightMarker: highlightedHotel.value?.position,
        };
      }

      return mapStore.markers.reduce<ProductSelectionMapData>(
        (acc, m: Marker) => {
          const suggestedCode = hotelFromItinerary.suggested?.code;
          const isSuggested = suggestedCode === m.data.hotelCode;
          const isSelected = heartedHotelCodes.includes(m.data.hotelCode);
          const isInspected =
            inspectedRef.value?.ingredientId === m.data.ingredientId &&
            inspectedRef.value?.hotelCode === m.data.hotelCode;

          if (isSelected && m.useContentFormatter === 'hotel-marker') {
            const state: PriceMarkerType = isInspected
              ? 'selected-inspected'
              : 'selected';
            const marker: PriceMarker = {
              productCode: m.data.hotelCode,
              ingredientId: m.data.ingredientId,
              point: m.position,
              label: m.data.formattedPrice,
              state,
            };
            return {
              markers: acc.markers,
              includedMarkers: acc.includedMarkers,
              selectedMarkers: [...acc.selectedMarkers, marker],
              highlightMarker: highlightedHotel.value?.position,
            };
          } else if (isSuggested && m.useContentFormatter === 'hotel-marker') {
            const state: PriceMarkerType = isInspected
              ? 'included-inspected'
              : 'included';
            const marker: PriceMarker = {
              productCode: m.data.hotelCode,
              ingredientId: m.data.ingredientId,
              point: m.position,
              label: m.data.formattedPrice,
              state,
            };
            return {
              markers: acc.markers,
              includedMarkers: [...acc.includedMarkers, marker],
              selectedMarkers: acc.selectedMarkers,
              highlightMarker: highlightedHotel.value?.position,
            };
          } else {
            let state: PriceMarkerType = 'normal';
            if (
              previouslySeen.value[`${m.data.ingredientId}${m.data.hotelCode}`]
            ) {
              state = 'previously-seen';
            }

            if (isInspected) {
              state = 'inspected';
            }

            const marker: PriceMarker = {
              productCode: m.data.hotelCode,
              ingredientId: m.data.ingredientId,
              point: m.position,
              label: m.data.formattedPrice,
              state,
            };

            return {
              markers: [...acc.markers, marker],
              includedMarkers: acc.includedMarkers,
              selectedMarkers: acc.selectedMarkers,
              highlightMarker: highlightedHotel.value?.position,
            };
          }
        },
        {
          markers: [],
          includedMarkers: [],
          selectedMarkers: [],
          highlightMarker: highlightedHotel.value?.position,
        }
      );
    });

    const hotelMapCardData = computed<HotelMapCardData | undefined>(() =>
      getHotelMapCardData(
        highlightedHotel.value,
        tripStore.getStaticInfo,
        getImageUrl
      )
    );

    const hotelMapCardType = computed<HotelMapCardType>(() => {
      if (browserStore.isSmallerThan900px) {
        return 'mobile';
      }

      if (isFullScreen.value) {
        return 'desktopExtended';
      }

      return 'desktopCollapsed';
    });

    return {
      // State
      highlightedHotel,
      isFullScreen: readonly(isFullScreen),
      savedBounds,
      mapRef,

      // Actions
      onToggleFullScreen,
      hotelCardClicked,
      hideHotelCard,
      hightlightOnMap,
      destroyMap,
      showHotelMapCard,
      inspect,
      resetInspect,
      resetSavedBound,
      resetMapInstance,
      zoomOnMarkerBounds,

      // Getters
      mapboxProperties,
      hotelMapCardData,
      hotelMapCardType,
    };
  }
);
